/**
 * The results plate's geometry as pure numbers — Blockari's
 * `render/results-layout.ts` and the board part of its `render/layout.ts`,
 * ported so the two titles put the plate and the docked tray in the same
 * place on the same viewport.
 *
 * 21 Wild's table is not a ten-by-ten board, but the plate does not sit on
 * the table: it is the shell's summary card, and Blockari sizes it from a
 * square board unit `B` derived from the viewport (640 on a desk, 358 on a
 * 390 phone). The same derivation here gives the same card, which is the
 * parity the kit grades.
 *
 * Every function is pure so it can be unit-tested against Blockari's own
 * numbers without a renderer.
 */

/** Blockari's board: ten columns, and the unit everything is measured in. */
const COLS = 10;

export interface BoardLayout {
  width: number;
  height: number;
  cell: number;
  gap: number;
  boardX: number;
  boardY: number;
  boardSize: number;
}

/**
 * Blockari's `computeLayout`, the board part: where a square board of side
 * `B` sits on this viewport, with its HUD above and hand below accounted for.
 * The plate is placed from this, so a 1920×1080 desk puts it at x 613 and a
 * 390×844 phone at x −8, exactly as Blockari does.
 */
export function boardLayout(width: number, height: number): BoardLayout {
  const compact = width < 600;
  const pad = compact ? 16 : 24;
  const chromeStrip = compact ? 56 : 0;
  const hudHeight = compact ? 110 : 112;
  const handHeight = compact ? Math.max(128, Math.min(180, height * 0.21)) : 200;
  const availW = width - pad * 2;
  const availH = height - chromeStrip - hudHeight - handHeight - pad * 2;
  const boardSize = Math.max(120, Math.floor(Math.min(availW, availH, 640)));
  const gap = Math.max(2, Math.round(boardSize / 100));
  const cell = (boardSize - gap * (COLS - 1)) / COLS;

  const stackH = hudHeight + boardSize + handHeight;
  let hudY: number;
  if (compact) {
    hudY = chromeStrip + pad * 0.5;
    const spare = Math.max(0, height - hudY - stackH - pad);
    const below = Math.min(48, spare);
    let left = spare - below;
    const grow = Math.min(80, left, Math.max(0, 202 - handHeight));
    left -= grow;
    hudY += left;
  } else {
    hudY = Math.max(pad, (height - stackH) / 2);
  }

  return {
    width,
    height,
    cell,
    gap,
    boardX: (width - boardSize) / 2,
    boardY: hudY + hudHeight,
    boardSize,
  };
}

/**
 * Below this board unit the kit's plate is not readable. Blockari's `computeLayout` floors the board at
 * 120, and on a viewport this short (a phone on its side, a window a few hundred px tall, a 320-wide
 * phone) it lands there: a plate 160 px wide with type to match, and a tray whose buttons are wider than
 * the plate they are docked to. The kit never shows a phone on its side; a player does.
 */
export const MIN_READABLE_B = 300;

/** The board unit the results use instead, at most: a portrait phone's (358), where the kit is graded. */
export const SHORT_B = 358;

/** The plate's side margin in the short layout, px. */
const SHORT_MARGIN = 12;

/**
 * The layout for a viewport too short for the kit's: the same plate at the phone's board unit (or the
 * widest that fits the width), centred, with the screen scrolling instead of the plate shrinking. Only
 * `boardX`, `boardSize`, `gap` and `cell` matter to the plate; the caller sets its top.
 */
export function shortLayout(width: number, height: number): BoardLayout {
  // The plate is (B + 2·pad)·0.98 wide with pad = gap·3 + 16; solve for the widest B that keeps the plate
  // inside the margins (two passes are enough: gap only changes by a pixel or two).
  let B = SHORT_B;
  for (let i = 0; i < 3; i += 1) {
    const gap = Math.max(2, Math.round(B / 100));
    const pad = gap * 3 + 16;
    B = Math.max(120, Math.min(SHORT_B, Math.floor((width - SHORT_MARGIN * 2) / 0.98 - pad * 2)));
  }
  const gap = Math.max(2, Math.round(B / 100));
  return {
    width,
    height,
    cell: (B - gap * (COLS - 1)) / COLS,
    gap,
    boardX: (width - B) / 2,
    boardY: 0,
    boardSize: B,
  };
}

/** Breakdown rows sit this fraction of the board size in from the board's edges. */
export const ROW_INSET = 0.14;

export interface PanelRect {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
  /** Breakdown column inset from the panel's edge (labels start, values end here). */
  inset: number;
}

/**
 * The plate's rectangle: the board plus `gap·3 + 16`, under Blockari's
 * pushed-in table transform (scale 0.98/0.92 about the centre, +10 px). The
 * tray docks to this edge, so the two must agree.
 */
export function resultsPanelRect(L: BoardLayout): PanelRect {
  const cx = L.boardX + L.boardSize / 2;
  const pad = L.gap * 3 + 16;
  const x0 = cx + (L.boardX - pad - cx) * 0.98;
  const x1 = cx + (L.boardX + L.boardSize + pad - cx) * 0.98;
  const cyB = L.boardY + L.boardSize / 2;
  const y0 = cyB + 10 + (L.boardY - pad - cyB) * 0.92;
  const y1 = cyB + 10 + (L.boardY + L.boardSize + pad - cyB) * 0.92;
  return {
    x: x0,
    y: y0,
    w: x1 - x0,
    h: y1 - y0,
    r: L.cell * 0.4,
    inset: L.boardX + L.boardSize * ROW_INSET - x0,
  };
}

/** Vertical metrics of the plate, as fractions of the board size. */
export const RESULTS_METRICS = {
  /** Plain results (solo, a round nobody answered): title, rule, score, rows. */
  plain: { title: 0.12, score: 0.26, best: 0.365, rows: 0.44 },
  /** Outcome results: the end reason as an eyebrow, the banner, score, stake line, vs line, rows. */
  outcome: { eyebrow: 0.07, banner: 0.165, score: 0.31, stake: 0.43, vs: 0.505, rows: 0.575 },
  rowPitch: 0.075,
  rowPitchMin: 0.055,
  /** From the last row's centre to the plate's bottom edge. */
  bottom: 0.065,
  /** The hero block's spacing may tighten to this factor before the VS line goes. */
  heroMin: 0.8,
  vsDrop: 0.05,
  eyebrowDrop: 0.06,
} as const;

/** The docked tray keeps this much clearance above the safe-area inset, and the plate this much under the safe top. */
export const TRAY_CLEARANCE_PX = 12;
export const PANEL_TOP_MARGIN_PX = 12;

export type HeroKind = 'plain' | 'outcome';

export interface FitInput {
  viewportH: number;
  safeTop: number;
  safeBottom: number;
  /** The plate's natural (unshifted) top and its plate-derived height. */
  panelY: number;
  plateH: number;
  B: number;
  /** The docked tray's full height; it overlaps the plate's bottom edge by `overlap` (the plate's radius). */
  trayH: number;
  overlap: number;
  hero: HeroKind;
  rows: number;
  /** Row indices that may collapse, least important first. */
  collapsible: readonly number[];
}

export type FitStage =
  | 'natural'
  | 'shift'
  | 'panel'
  | 'pitch'
  | 'collapse'
  | 'eyebrow'
  | 'hero'
  | 'vs'
  | 'overflow';

export interface Fit {
  /** Vertical shift of the whole ceremony (<= 0). */
  shift: number;
  /** Row pitch in px. */
  pitch: number;
  hidden: readonly number[];
  /** Hero spacing factor (1 = natural, down to `heroMin`). */
  k: number;
  vs: boolean;
  eyebrow: boolean;
  panelH: number;
  /** Where the first row's centre sits below the plate's top, in px. */
  rowsY: number;
  fits: boolean;
  stage: FitStage;
}

/** Where the rows start below the plate's top, in board units, for a hero at spacing `k`. */
export function rowsStart(hero: HeroKind, k: number, vs: boolean, eyebrow = true): number {
  const m = RESULTS_METRICS;
  if (hero === 'plain') return m.plain.rows * k;
  return m.outcome.rows * k - (vs ? 0 : m.vsDrop) - (eyebrow ? 0 : m.eyebrowDrop);
}

function contentH(
  i: FitInput,
  pitch: number,
  shown: number,
  k: number,
  vs: boolean,
  eyebrow = true,
): number {
  return (
    rowsStart(i.hero, k, vs, eyebrow) * i.B +
    Math.max(0, shown - 1) * pitch +
    RESULTS_METRICS.bottom * i.B
  );
}

/**
 * The compression order when the plate plus the tray would not fit: shift
 * up → tighten the rows' pitch → collapse the least important rows → drop
 * the eyebrow → tighten the hero → drop the VS line. Never the score, the
 * outcome, the stake line, LEVEL REACHED or the tray. Each stage is tried
 * only when the one before still overflows.
 */
export function resultsFit(i: FitInput): Fit {
  const m = RESULTS_METRICS;
  const P = m.rowPitch * i.B;
  const Pmin = m.rowPitchMin * i.B;
  const limit = i.viewportH - i.safeBottom - TRAY_CLEARANCE_PX - (i.trayH - i.overlap);
  const maxShift = Math.max(0, i.panelY - i.safeTop - PANEL_TOP_MARGIN_PX);
  const make = (
    stage: FitStage,
    shift: number,
    pitch: number,
    hidden: readonly number[],
    k: number,
    vs: boolean,
    eyebrow: boolean,
  ): Fit => {
    const shown = i.rows - hidden.length;
    const content = contentH(i, pitch, shown, k, vs, eyebrow);
    const panelH = stage === 'natural' || stage === 'shift' ? Math.max(i.plateH, content) : content;
    return {
      shift,
      pitch,
      hidden,
      k,
      vs,
      eyebrow,
      panelH,
      rowsY: rowsStart(i.hero, k, vs, eyebrow) * i.B,
      fits: i.panelY + shift + panelH <= limit + 0.5,
      stage,
    };
  };

  const naturalH = Math.max(i.plateH, contentH(i, P, i.rows, 1, true));
  if (i.panelY + naturalH <= limit) return make('natural', 0, P, [], 1, true, true);
  const shift = -Math.min(maxShift, Math.ceil(i.panelY + naturalH - limit));
  if (i.panelY + shift + naturalH <= limit) return make('shift', shift, P, [], 1, true, true);
  const fullShift = -maxShift;
  const top = i.panelY + fullShift;
  const avail = limit - top;
  if (contentH(i, P, i.rows, 1, true) <= avail) return make('panel', fullShift, P, [], 1, true, true);
  const pitchFor = (shown: number, k: number, vs: boolean, eyebrow: boolean): number =>
    shown > 1
      ? (avail - rowsStart(i.hero, k, vs, eyebrow) * i.B - m.bottom * i.B) / (shown - 1)
      : Number.POSITIVE_INFINITY;
  let need = pitchFor(i.rows, 1, true, true);
  if (need >= Pmin) return make('pitch', fullShift, Math.min(P, need), [], 1, true, true);
  const hidden: number[] = [];
  let eyebrow = true;
  for (const idx of i.collapsible) {
    if (hidden.length === 2 && eyebrow && i.hero === 'outcome') {
      eyebrow = false;
      need = pitchFor(i.rows - hidden.length, 1, true, false);
      if (need >= Pmin) return make('eyebrow', fullShift, Math.min(P, need), [...hidden], 1, true, false);
    }
    hidden.push(idx);
    need = pitchFor(i.rows - hidden.length, 1, true, eyebrow);
    if (need >= Pmin) return make('collapse', fullShift, Math.min(P, need), [...hidden], 1, true, eyebrow);
  }
  if (eyebrow && i.hero === 'outcome') {
    eyebrow = false;
    need = pitchFor(i.rows - hidden.length, 1, true, false);
    if (need >= Pmin) return make('eyebrow', fullShift, Math.min(P, need), [...hidden], 1, true, false);
  }
  const shown = i.rows - hidden.length;
  for (let k = 0.95; k >= m.heroMin - 1e-9; k -= 0.05) {
    if (pitchFor(shown, k, true, eyebrow) >= Pmin) return make('hero', fullShift, Pmin, [...hidden], k, true, eyebrow);
  }
  if (i.hero === 'outcome' && pitchFor(shown, m.heroMin, false, eyebrow) >= Pmin) {
    return make('vs', fullShift, Pmin, [...hidden], m.heroMin, false, eyebrow);
  }
  const vs = i.hero !== 'outcome';
  return make('overflow', fullShift, Pmin, [...hidden], m.heroMin, vs, eyebrow);
}

/** A settled challenge as the plate needs it. */
export interface ResultsOutcome {
  won: boolean;
  /** $CHAIN won (the payout) or lost (the stake); 0 for a challenge with nothing staked. */
  chain: number;
  myScore: number;
  theirScore: number;
  myLevel?: number | undefined;
  theirLevel?: number | undefined;
}

export interface OutcomeCopy {
  /** "YOU WON" / "YOU LOST". */
  title: string;
  /** "+20 $CHAIN" / "−10 $CHAIN" (a real minus sign); empty with nothing staked. */
  stake: string;
  /** "8,470 vs 5,120 · LV 8 vs LV 4"; the levels only when both are known. */
  vs: string;
}

const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');

export function outcomeCopy(o: ResultsOutcome): OutcomeCopy {
  const title = o.won ? 'YOU WON' : 'YOU LOST';
  const stake = o.chain > 0 ? `${o.won ? '+' : '−'}${fmt(o.chain)} $CHAIN` : '';
  const levels =
    o.myLevel !== undefined && o.theirLevel !== undefined
      ? ` · LV ${o.myLevel} vs LV ${o.theirLevel}`
      : '';
  return { title, stake, vs: `${fmt(o.myScore)} vs ${fmt(o.theirScore)}${levels}` };
}

/** The end reason as the plate's headline (plain) or eyebrow (outcome), in 21 Wild's words. */
export function endReasonCopy(reason: 'deckEmpty' | 'bustedOut' | 'timeout'): string {
  return reason === 'deckEmpty' ? 'DECK CLEARED' : reason === 'bustedOut' ? 'BUSTED OUT' : "TIME'S UP";
}
